import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface TableColumn {
  key: string;
  label: string;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

@Component({
  selector: 'app-molecule-table',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent {
  @Input() columns: TableColumn[] = [];
  @Input() data: any[] = [];
  @Input() loading: boolean = false;
  @Input() striped: boolean = true;
  @Input() hover: boolean = true;
  @Input() bordered: boolean = false;
  @Input() compact: boolean = false;
  @Input() emptyMessage: string = 'No data available';
  @Input() sortKey: string = '';
  @Input() sortDirection: 'asc' | 'desc' = 'asc';

  @Output() rowClick = new EventEmitter<any>();
  @Output() sort = new EventEmitter<{ key: string; direction: 'asc' | 'desc' }>();

  get tableClasses(): string {
    const classes = ['min-w-full', 'divide-y', 'divide-gray-200'];

    if (this.bordered) {
      classes.push('border', 'border-gray-200');
    }

    return classes.join(' ');
  }

  get tbodyClasses(): string {
    const classes = ['bg-white', 'divide-y', 'divide-gray-200'];
    return classes.join(' ');
  }

  getRowClasses(index: number): string {
    const classes: string[] = [];

    if (this.hover) {
      classes.push('hover:bg-gray-50', 'cursor-pointer', 'transition-colors');
    }

    if (this.striped && index % 2 === 1) {
      classes.push('bg-gray-50');
    }

    return classes.join(' ');
  }

  getCellClasses(column: TableColumn): string {
    const classes = ['text-sm', 'text-gray-900'];

    if (this.compact) {
      classes.push('px-4', 'py-2');
    } else {
      classes.push('px-6', 'py-4');
    }

    if (column.align === 'center') {
      classes.push('text-center');
    } else if (column.align === 'right') {
      classes.push('text-right');
    } else {
      classes.push('text-left');
    }

    return classes.join(' ');
  }

  getHeaderClasses(column: TableColumn): string {
    const classes = ['text-xs', 'font-medium', 'text-gray-500', 'uppercase', 'tracking-wider'];

    if (this.compact) {
      classes.push('px-4', 'py-2');
    } else {
      classes.push('px-6', 'py-3');
    }

    if (column.align === 'center') {
      classes.push('text-center');
    } else if (column.align === 'right') {
      classes.push('text-right');
    } else {
      classes.push('text-left');
    }

    if (column.sortable) {
      classes.push('cursor-pointer', 'select-none', 'hover:text-gray-700');
    }

    return classes.join(' ');
  }

  onRowClick(row: any): void {
    this.rowClick.emit(row);
  }

  onSort(column: TableColumn): void {
    if (!column.sortable) return;

    const newDirection =
      this.sortKey === column.key && this.sortDirection === 'asc' ? 'desc' : 'asc';

    this.sort.emit({ key: column.key, direction: newDirection });
  }

  getSortIcon(column: TableColumn): string {
    if (!column.sortable) return '';
    if (this.sortKey !== column.key) return '↕';
    return this.sortDirection === 'asc' ? '↑' : '↓';
  }

  getCellValue(row: any, key: string): any {
    return key.split('.').reduce((obj, k) => obj?.[k], row);
  }
}
